/**
 * Phase 61 Plan 06 — Respect admin API client.
 *
 * Typed wrappers around the 8 admin endpoints at /api/admin/respect/*.
 * All are gated admin-only on the backend (D-19).
 */
import { apiGet, apiPost, apiPatch } from './client';

// ── DTOs (mirror backend respectAdminSchemas.ts) ──────────────────────────────

export interface CategoryTagDTO {
  id: number;
  label: string;
  weight: number;
  disabledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RespectWeightsDTO {
  id: number;
  kudosBasePoints: number;
  onTimeTimesheetPoints: number;
  slaResponsivenessPoints: number;
  engineeringCommitActivityPoints: number;
  projectClosureQualityPoints: number;
  teamEventAttendancePoints: number;
  updatedAt: string;
  updatedById: number | null;
}

export interface BandCounts {
  small: number;
  medium: number;
  'spotlight-level': number;
}

export interface PreviewResultDTO {
  current: BandCounts;
  proposed: BandCounts;
}

// ── Input types ───────────────────────────────────────────────────────────────

export interface CreateCategoryTagInput {
  label: string;
  weight?: number;
}

export interface UpdateCategoryTagInput {
  label?: string;
  weight?: number;
}

export interface UpdateWeightsInput {
  kudosBasePoints?: number;
  onTimeTimesheetPoints?: number;
  slaResponsivenessPoints?: number;
  engineeringCommitActivityPoints?: number;
  projectClosureQualityPoints?: number;
  teamEventAttendancePoints?: number;
}

export interface PreviewWeightInput extends UpdateWeightsInput {
  tagWeights?: Record<string, number>;
}

// ── API calls ─────────────────────────────────────────────────────────────────

export async function listCategoryTags(): Promise<{ data: CategoryTagDTO[] }> {
  return apiGet('/admin/respect/category-tags');
}

export async function createCategoryTag(
  input: CreateCategoryTagInput,
): Promise<{ data: CategoryTagDTO }> {
  return apiPost('/admin/respect/category-tags', input);
}

export async function updateCategoryTag(
  id: number,
  input: UpdateCategoryTagInput,
): Promise<{ data: CategoryTagDTO }> {
  return apiPatch(`/admin/respect/category-tags/${id}`, input);
}

export async function disableCategoryTag(id: number): Promise<{ data: CategoryTagDTO }> {
  return apiPost(`/admin/respect/category-tags/${id}/disable`);
}

export async function reenableCategoryTag(id: number): Promise<{ data: CategoryTagDTO }> {
  return apiPost(`/admin/respect/category-tags/${id}/reenable`);
}

export async function getWeights(): Promise<{ data: RespectWeightsDTO }> {
  return apiGet('/admin/respect/weights');
}

export async function updateWeights(
  input: UpdateWeightsInput,
): Promise<{ data: RespectWeightsDTO }> {
  return apiPatch('/admin/respect/weights', input);
}

export async function previewWeightImpact(
  input: PreviewWeightInput,
): Promise<{ data: PreviewResultDTO }> {
  return apiPost('/admin/respect/preview', input);
}
